import React, { Fragment, useMemo } from "react"

import { Box, useTheme } from "@mui/material"

import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"

import { PageCard, PageCardContent } from "components/card"
import { EmptyData } from "components/EmptyData"
import { StoreIcon } from "components/icons"
import { VenueSelector } from "components/VenueSelector"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { DirectorySkeletonCard } from "components/domains/directory"
import { DirectoryOccupantCard } from "extensions/iconsiam/components/domains/directory/DirectoryOccupantCard"
import { MotionDiv } from "components/motions"
import { useDirectory } from "extensions/iconsiam/hooks/directory/useDirectory"
import { SidebarMenu } from "extensions/iconsiam/components/navigation/kiosk-h/SidebarMenu"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { HorizontalScrollBarWithButton } from "extensions/iconsiam/components/HorizontalScrollBar/HorizontalScrollBarWithButton"

import { AnimatePresence } from "framer-motion"
import { SubCategorySidebar } from "extensions/iconsiam/components/domains/directory"
import {
  LTRSidebarVariant,
  LTRContentVariant,
  RTLSidebarVariant,
  RTLContentVariant,
} from "extensions/iconsiam/components/domains/motions/variants"
import { useElementScrollRestoration } from "hooks/scrollbar/useElementScrollRestoration"

export const DirectoryKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
}) => {
  const { t } = useTranslation()
  const {
    handleChangeSubCategory,
    changeVenueFilter,
    handleChangeParentCategory,
    parentCategoryId,
    sortedParentCategoryWithOccupant,
    filteredOccupantsSorted,
    subCategoryWithActiveParentCategory,
    subCategoryId,
    venueFilter,
    venues,
    isMultiVenueProject,
    locationVenueId,
    hasParentCategory,
    showSubCategoryFilter,
    endScrollRef,
    hasNextPage,
    hasData,
    parentCategory,
  } = useDirectory()

  const theme = useTheme()

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.directory) || <StoreIcon />,
    [theme]
  )
  const styleOverrides = theme.components[`DirectoryPage`]?.styleOverrides || {}
  const {
    pageCard,
    pageCardContent,
    gridContainerWrapper,
    gridContainer,
    gridItem,
    subCategoryFilterBar,
    emptyDataWrapper,
    categoryFilterBar,
    filterSection,
    venueSelector,
    contentBox,
    motionDivStyle,
  } = styleOverrides

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  const { scrollRef } = useElementScrollRestoration<HTMLDivElement>(
    "directory-page-scroll-pane"
  )

  const isRtl = theme.isRtl
  const sidebarVariants = isRtl ? RTLSidebarVariant : LTRSidebarVariant
  const contentVariants = isRtl ? RTLContentVariant : LTRContentVariant

  return (
    //@ts-ignore
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent sx={pageCardContent}>
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            sx={motionDivStyle}
          >
            <Box sx={contentBox}>
              <AnimatePresence mode="wait">
                {subCategoryWithActiveParentCategory.length > 0 && (
                  <MotionDiv
                    key="sidebar"
                    variants={sidebarVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <SubCategorySidebar
                      mainCategory={parentCategory}
                      SubCategoryComponent={
                        <CategoryFilterBar
                          sx={subCategoryFilterBar}
                          activeCategory={subCategoryId}
                          categories={subCategoryWithActiveParentCategory}
                          onChange={handleChangeSubCategory}
                          renderFilterButton={CategoryFilterChip}
                        />
                      }
                    />
                  </MotionDiv>
                )}
              </AnimatePresence>
              {hasData ? (
                <Box sx={gridContainerWrapper} ref={scrollRef}>
                  <MotionDiv
                    layout="preserve-aspect"
                    layoutDependency={showSubCategoryFilter}
                    sx={gridContainer}
                    variants={contentVariants.kioskH}
                    initial="initial"
                    animate={showSubCategoryFilter ? "withSidebar" : "animate"}
                  >
                    {filteredOccupantsSorted.pages.map((page, pageNum) => {
                      return (
                        <Fragment key={pageNum}>
                          {page.data.map((occupant) => {
                            const isAd = occupant?.properties?.isAd
                            const occupantId = occupant?.id
                            const isNewShop = occupant?.properties?.isNewShop
                            const isComingSoon =
                              occupant?.properties?.isComingSoon
                            return (
                              <Box
                                id={occupantId}
                                data-testid={occupantId}
                                sx={gridItem}
                                key={`${
                                  isAd ? "ad-" : "directory-item"
                                }${occupantId}`}
                              >
                                <DirectoryOccupantCard
                                  data={occupant}
                                  isNewShop={isNewShop}
                                  isComingSoon={!isAd && isComingSoon}
                                />
                              </Box>
                            )
                          })}
                        </Fragment>
                      )
                    })}
                    {hasNextPage &&
                      Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                        <DirectorySkeletonCard
                          ref={
                            index === SKELETON_INDEX_TO_REF
                              ? endScrollRef
                              : null
                          }
                          key={index}
                          sx={gridItem}
                        />
                      ))}
                  </MotionDiv>
                </Box>
              ) : (
                <Box sx={emptyDataWrapper}>
                  <EmptyData
                    icon={emptyIcon}
                    primary={t("Coming soon")}
                    secondary={t("Stay tuned for the update")}
                  />
                </Box>
              )}
            </Box>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={changeVenueFilter}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            {hasParentCategory && (
              <Box sx={filterSection}>
                <HorizontalScrollBarWithButton className="category-filterbar">
                  <CategoryFilterBar
                    sx={categoryFilterBar}
                    activeCategory={parentCategoryId}
                    categories={sortedParentCategoryWithOccupant}
                    onChange={handleChangeParentCategory}
                  />
                </HorizontalScrollBarWithButton>
              </Box>
            )}
          </MotionDiv>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
